import { chunk } from 'lodash'
import { useEffect, useMemo, useState } from 'preact/hooks'
import { toBinary, toBinaryPadded, getBit, setBit } from '~/utils'

const COLS = 4
const ROWS = 8
const BYTE_LENGTH = 8
const CELLS = 256 / BYTE_LENGTH
const SALT = 0
const MASKS = [
	0b00000000000000000000000011111111, 0b00000000000000001111111100000000,
	0b00000000111111110000000000000000, 0b11111111000000000000000000000000,
]

const encoder = new TextEncoder()

function HashTable({ text, salt }: { text: string; salt: number }) {
	const compressed: string[] = useMemo(() => {
		const filteredText = text.replace(/[^a-zA-Z0-9]/g, '')
		const buffer = encoder.encode(filteredText)
		const binaryBuffer: number[] = Array.from(buffer).map(
			(char, i) => char << ((i % COLS) * BYTE_LENGTH),
		)
		return chunk<number>(binaryBuffer, COLS)
			.map((group) => group.reduce((val, next) => val | next, salt))
			.map((raw) => toBinaryPadded(raw))
	}, [salt, text])
	const [hash, setHash] = useState<string>('')

	useEffect(() => {
		let cancelled = false

		window.crypto.subtle
			.digest('SHA-256', encoder.encode(compressed.join('')))
			.then((data) => {
				const uint8View = new Uint8Array(data)
				if (!cancelled) {
					setHash(
						Array.from(uint8View)
							.map((b) => b.toString(16).padStart(2, '0'))
							.join(''),
					)
				}
			})
			.catch((e: unknown) => {
				console.error('Error digesting data', e)
			})

		return () => {
			cancelled = true
		}
	}, [text, compressed])

	return (
		<>
			{compressed.map((bits, i) => (
				<span key={`${bits}-${i}`}>{bits}</span>
			))}
			<span>{hash}</span>
		</>
	)
}

export default HashTable
